import React from 'react';

import { Text } from '@eluve/components';

import { formatMeasureValue } from '../measures';

/**
 * The drawer's full-width trend chart: value + date labels per point, a
 * dashed accent segment out to a pending proposed value, halo rings on
 * amended points, and a legend. Pure SVG — same dependency-light stance as
 * TrendSparkline, just bigger and labeled.
 */
export type ChartPoint = {
  value: number;
  dateLabel: string;
  amended?: boolean;
};

export type TrendChartProps = {
  /** Validated points, oldest first. */
  points: ChartPoint[];
  /** A pending draft appended after the last validated point. */
  proposed?: { value: number; dateLabel: string };
  /** Unit suffix rendered after each value label. */
  unit?: string;
};

const WIDTH = 560;
const HEIGHT = 200;
// Side margins fit the centered first/last date labels.
const MARGIN = { left: 44, right: 44, top: 20, bottom: 28 };

export const TrendChart: React.FC<TrendChartProps> = ({
  points,
  proposed,
  unit,
}) => {
  const total = points.length + (proposed ? 1 : 0);
  if (total < 2) return null;

  const values = points.map((p) => p.value);
  if (proposed) values.push(proposed.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  const pad = (max - min) * 0.2 || 2;
  min -= pad;
  max += pad;

  const x = (index: number) =>
    MARGIN.left + index * ((WIDTH - MARGIN.left - MARGIN.right) / (total - 1));
  const y = (value: number) =>
    MARGIN.top +
    ((max - value) / (max - min)) * (HEIGHT - MARGIN.top - MARGIN.bottom);

  const valueLabel = (value: number) => formatMeasureValue(value, unit);
  const lastSolidPoint = points[points.length - 1];

  return (
    <div data-testid="trend-chart">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height="auto"
        fill="none"
        className="block overflow-visible"
        role="img"
        aria-label="Value history chart"
      >
        {points.length >= 2 && (
          <polyline
            points={points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')}
            className="stroke-current text-text-positive"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        )}
        {proposed && lastSolidPoint && (
          <line
            x1={x(points.length - 1)}
            y1={y(lastSolidPoint.value)}
            x2={x(points.length)}
            y2={y(proposed.value)}
            className="stroke-current text-text-insights"
            strokeWidth={2}
            strokeDasharray="5 4"
            strokeLinecap="round"
          />
        )}
        {points.map((point, index) => (
          <React.Fragment key={index}>
            <circle
              cx={x(index)}
              cy={y(point.value)}
              r={3.5}
              className="fill-current text-text-positive"
            />
            {point.amended && (
              <circle
                cx={x(index)}
                cy={y(point.value)}
                r={6}
                fill="none"
                className="stroke-current text-text-positive"
                strokeWidth={1.2}
                opacity={0.5}
              />
            )}
            <text
              x={x(index)}
              y={y(point.value) - 9}
              textAnchor="middle"
              className="fill-current text-text-primary"
              style={{ fontSize: 11, fontWeight: 600 }}
            >
              {valueLabel(point.value)}
            </text>
            <text
              x={x(index)}
              y={HEIGHT - 8}
              textAnchor="middle"
              className="fill-current text-text-tertiary"
              style={{ fontSize: 11 }}
            >
              {point.dateLabel}
            </text>
          </React.Fragment>
        ))}
        {proposed && (
          <>
            <circle
              cx={x(points.length)}
              cy={y(proposed.value)}
              r={4.2}
              className="fill-bg-primary stroke-current text-text-insights"
              strokeWidth={2}
            />
            <text
              x={x(points.length)}
              y={y(proposed.value) - 10}
              textAnchor="middle"
              className="fill-current text-text-insights"
              style={{ fontSize: 11, fontWeight: 600 }}
            >
              {valueLabel(proposed.value)}
            </text>
            <text
              x={x(points.length)}
              y={HEIGHT - 8}
              textAnchor="middle"
              className="fill-current text-text-insights"
              style={{ fontSize: 11, fontWeight: 600 }}
            >
              {proposed.dateLabel}
            </text>
          </>
        )}
      </svg>
      <div className="flex justify-center gap-5 pt-1">
        <LegendItem tone="text-text-positive" label="Validated" />
        {proposed && (
          <LegendItem
            tone="text-text-insights"
            label="Draft (proposed)"
            dashed
            hollow
          />
        )}
      </div>
    </div>
  );
};

const LegendItem: React.FC<{
  tone: string;
  label: string;
  dashed?: boolean;
  hollow?: boolean;
}> = ({ tone, label, dashed, hollow }) => (
  <span className="flex items-center gap-1.5">
    <svg width={22} height={10} viewBox="0 0 22 10" className={tone}>
      <line
        x1={1}
        y1={5}
        x2={21}
        y2={5}
        stroke="currentColor"
        strokeWidth={2}
        strokeDasharray={dashed ? '4 3' : undefined}
      />
      {hollow && (
        <circle
          cx={11}
          cy={5}
          r={3}
          className="fill-bg-primary"
          stroke="currentColor"
          strokeWidth={1.6}
        />
      )}
    </svg>
    <Text inline size="xs" color="tertiary" weight="regular">
      {label}
    </Text>
  </span>
);
